import { INDICATOR_DEFAULTS, type Indicators } from './indicators.ts';
import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_REGION, getRegion } from '../../../shared/regions';

export interface Settings {
  unit: 'm' | 'yd';
  /** Country whose handicap body scores are posted to. */
  regionCode: string;
  /**
   * null until the golfer picks a course.
   *
   * There is no sensible default here. Defaulting to a demo layout put an
   * invented hole 1 on the map over whatever happened to be underneath the
   * golfer — which reads as a broken app, not as a placeholder.
   */
  courseId: string | null;
  /** Remembered so a failed reload can still name the course it was loading. */
  courseName: string | null;
  buggyId: string;
  teeColour: string;
  /** Pro shop / marshal build shows the fleet screen and hides scoring. */
  role: 'buggy' | 'shop';
  /** Head units get hot in the sun; a dark, high contrast theme is default. */
  theme: 'dark' | 'light';
  bigText: boolean;
  keepAwake: boolean;
  demoDrive: boolean;
  /** Pen the map to the hole being played — stops wandering, bounds tile load. */
  lockToHole: boolean;
  /** Pin the hole number so GPS can never move it. Manual buttons still work. */
  lockHoleNumber: boolean;
  /** Spoken distances and hole announcements. */
  voice: boolean;
  /**
   * Turn the map with the phone, using the compass.
   *
   * On by default: a heading-up map is what makes a glance work, and GPS course
   * cannot supply one standing still. Off is for anyone who finds a rotating
   * map harder to read than a fixed one, which is a real preference.
   */
  compass: boolean;
  /**
   * Which basemap the navigation screen draws.
   *
   * Not a cosmetic choice: road is the only one that reads at speed, satellite
   * is the only one that shows the actual bunker, and hybrid is the only one
   * that does both — which is what a buggy on a course usually wants.
   */
  mapMode: 'poly' | 'road' | 'satellite' | 'hybrid';
  /**
   * Golf overlays on the one map: hole corridor, green, hazards, yardages,
   * hole ‹ ›. Off = a plain positioned map, which is what driving to the
   * course wants. This is the switch that makes one map do both jobs.
   */
  courseMode: boolean;
  /** The red buggy cart-path network. */
  cartPathMode: boolean;
  /** What the map is allowed to draw. Read by every surface, CarPlay included. */
  indicators: Indicators;
}

const KEY = 'fairway.settings.v1';

/** The retired built-in demo layout. Treated as "no course chosen". */
const LEGACY_DEMO_ID = 'demo-parkland-18';

const DEFAULTS: Settings = {
  unit: getRegion(DEFAULT_REGION).unit,
  regionCode: DEFAULT_REGION,
  courseId: null,
  courseName: null,
  buggyId: '',
  teeColour: 'White',
  role: 'buggy',
  bigText: true,
  keepAwake: true,
  demoDrive: false,
  lockToHole: true,
  lockHoleNumber: false,
  voice: false,
  compass: true,
  mapMode: 'satellite',
  theme: 'dark',
  courseMode: true,
  cartPathMode: true,
  indicators: INDICATOR_DEFAULTS,
};

/**
 * Provisioning handshake.
 *
 * A unit is flashed by launching it once at
 *   /?buggy=BUGGY-07&key=<deviceKey>&course=<courseId>
 * which the provisioning script fires as an Android intent. The values are
 * persisted and stripped from the URL immediately, so the key never survives in
 * history, and a factory-reset unit re-provisions with the same one-liner.
 */
function consumeProvisioning(): Partial<Settings> {
  const q = new URLSearchParams(location.search);
  const buggy = q.get('buggy');
  const key = q.get('key');
  const course = q.get('course');
  const role = q.get('role');
  const rgn = q.get('region');
  if (!buggy && !key && !course && !role && !rgn) return {};

  if (key) localStorage.setItem('fairway.deviceKey', key);
  const patch: Partial<Settings> = {};
  if (buggy) patch.buggyId = buggy.toUpperCase();
  if (course) patch.courseId = course;
  if (role === 'shop' || role === 'buggy') patch.role = role;
  if (rgn) {
    const code = rgn.toUpperCase();
    patch.regionCode = code;
    patch.unit = getRegion(code).unit;
  }

  history.replaceState(null, '', location.pathname);
  return patch;
}

function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Settings>) : {};
    const s = { ...DEFAULTS, ...parsed, ...consumeProvisioning() };
    // Anyone who ran a build before the demo course was retired has its id
    // persisted. Changing the default alone would not reach them, and they are
    // exactly the people who saw the invented hole.
    if (s.courseId === LEGACY_DEMO_ID) s.courseId = null;
    if (!s.buggyId) {
      // Provisioning writes this at flash time; fall back to a stable local id
      // so an un-provisioned unit still reports something unique.
      s.buggyId =
        localStorage.getItem('fairway.buggyId') ??
        `BUGGY-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    }
    localStorage.setItem('fairway.buggyId', s.buggyId);
    return s;
  } catch {
    return DEFAULTS;
  }
}

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(load);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(settings));
    localStorage.setItem('fairway.buggyId', settings.buggyId);
  }, [settings]);

  const update = useCallback(
    (patch: Partial<Settings>) => setSettings((s) => ({ ...s, ...patch })),
    []
  );

  return { settings, update };
}

/** Head units blank the screen mid-round otherwise. */
export function useWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        lock = await navigator.wakeLock.request('screen');
      } catch {
        /* denied or unsupported */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => {});
    };
  }, [enabled]);
}
